import { toast } from "sonner";
import $api from "@/zapi-frontend/shared/api/axios";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { TChapter } from "@/zapi-frontend/shared/types/projects";
import { convertMarkdownLinksToHtml } from "@/zapi-frontend/utils/markdown";
import { trackMemoSave } from "@/zapi-frontend/shared/lib/pendingMemoSaves";

const OVERVIEW_REGEN_DEBOUNCE_MS = 4000;
const overviewRegenTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const scheduleOverviewRegeneration = (projectId: string) => {
  const existing = overviewRegenTimeouts.get(projectId);
  if (existing) clearTimeout(existing);

  const timeoutId = setTimeout(async () => {
    overviewRegenTimeouts.delete(projectId);
    const { syncOverviewIfNeeded } = useProjectStore.getState();
    const [result] = await Promise.allSettled([syncOverviewIfNeeded(projectId)]);
    if (result.status === "rejected") {
      const status = result.reason?.response?.status;
      if (status !== 400) {
        toast.error("Failed to sync overview");
      }
      console.error("Error syncing overview:", result.reason);
    }
  }, OVERVIEW_REGEN_DEBOUNCE_MS);

  overviewRegenTimeouts.set(projectId, timeoutId);
};

const saveContent = async (markdown: string, projectId: string, chapterId: string, current?: boolean) => {
  try {
    const response = await trackMemoSave(
      projectId,
      chapterId,
      $api.put<{ data: TChapter }>(`/projects/${projectId}/chapters/${chapterId}`, {
        memo: markdown,
        current
      })
    );

    if (response.status !== 200) throw new Error("Failed to save content");

    return response.data.data;
  } catch (error) {
    console.error("Error saving content:", error);
    throw error;
  }
};

export const createAutoSaveHandler = (
  autoSave: boolean,
  projectId?: string,
  iAmLeader?: () => boolean,
  chapterId?: number,
  isCurrent?: boolean,
  chapterSlug?: string
) => {
  // The target chapter is captured when the handler is created (i.e. the chapter
  // active at that render), NOT read from the store at call time. This keeps a
  // debounced save that fires after the user has switched chapters pointed at the
  // chapter it was scheduled for, instead of the newly-active one.
  // `force` bypasses the live leader check for flushes that run after the Yjs
  // provider is torn down (navigation/unmount) — leadership was already
  // verified when the edit was scheduled.
  return async (markdown: string, force = false) => {
    if (!autoSave || !projectId) return;
    if (!force && !iAmLeader?.()) return;
    if (!chapterId) return;

    try {
      const { updateChapterContentUpdatedAt, projectChapters } = useChapterStore.getState();
      const previousContentUpdatedAt = projectChapters.find((chapter) => chapter.id === chapterId)?.activeMemo
        ?.contentUpdatedAt;

      // Don't normalize content to preserve raw text exactly as typed
      const updatedChapter = await saveContent(
        convertMarkdownLinksToHtml(markdown),
        projectId,
        chapterId.toString(),
        isCurrent
      );

      if (updatedChapter.activeMemo?.contentUpdatedAt) {
        updateChapterContentUpdatedAt(chapterId.toString(), updatedChapter.activeMemo.contentUpdatedAt);
      }

      const contentChanged = updatedChapter.activeMemo?.contentUpdatedAt !== previousContentUpdatedAt;
      if (chapterSlug === "executive-summary" && contentChanged) {
        scheduleOverviewRegeneration(projectId);
      }
    } catch (error) {
      console.error("Auto-save failed:", error);
    }
  };
};
